import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
interface RulerVisualProps {
  length?: number;
  objectStart?: number;
  objectEnd?: number;
  unit?: 'cm' | 'mm';
  label?: string;
  mode?: 'stimulus' | 'support';
  hideValue?: boolean;
}
export function RulerVisual({
  length = 15,
  objectStart,
  objectEnd,
  unit = 'cm',
  label,
  mode = 'support',
  hideValue = false
}: RulerVisualProps) {
  const computed = useMemo(() => {
    const hasObject = objectStart !== undefined && objectEnd !== undefined;
    const objectLength = hasObject ? Math.abs(objectEnd - objectStart) : 0;
    const start = hasObject ? Math.min(objectStart, objectEnd) : 0;
    const end = hasObject ? Math.max(objectStart, objectEnd) : 0;
    // Cap at 15 for display purposes
    const displayLength = Math.min(length, 15);
    return {
      hasObject,
      objectLength,
      start,
      end,
      displayLength
    };
  }, [length, objectStart, objectEnd]);
  const rulerViz = <div className="flex flex-col items-center gap-2 w-full" data-id="element-9924">
      {/* Object being measured (if provided) */}
      {computed.hasObject && <div className="relative w-full h-8" data-id="element-9925">
          <motion.div initial={{
        scaleX: 0,
        opacity: 0
      }} animate={{
        scaleX: 1,
        opacity: 1
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="absolute top-0 bg-blue-400 rounded-md shadow-sm" style={{
        left: `${computed.start / computed.displayLength * 100}%`,
        width: `${computed.objectLength / computed.displayLength * 100}%`,
        height: '24px',
        transformOrigin: 'left center'
      }} data-id="element-9926">
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-9927">
              <div className="bg-white/90 px-2 py-0.5 rounded text-[10px] font-bold text-blue-900" data-id="element-9928">
                {!hideValue || mode === 'support' ? `${computed.objectLength} ${unit}` : '?'}
              </div>
            </div>
          </motion.div>
        </div>}

      {/* Ruler */}
      <div className="w-full relative h-12 bg-amber-200 rounded-sm border border-amber-600 shadow-sm overflow-hidden" data-id="element-9929">
        {/* Centimeter markings */}
        {Array.from({
        length: computed.displayLength + 1
      }, (_, i) => {
        const leftPercent = i / computed.displayLength * 100;
        const isFive = i % 5 === 0;
        return <div key={`cm-${i}`} className="absolute top-0 flex flex-col items-center" style={{
          left: `${leftPercent}%`,
          transform: 'translateX(-50%)'
        }} data-id="element-9930">
                <div className={`bg-amber-800 ${isFive ? 'h-4 w-0.5' : 'h-3 w-px'}`} data-id="element-9931" />
                <span className="text-[10px] font-bold text-amber-900 mt-0.5" data-id="element-9932">
                  {i}
                </span>
              </div>;
      })}

        {/* Millimeter markings (between cm marks) */}
        {unit === 'mm' && Array.from({
        length: computed.displayLength * 10
      }, (_, i) => {
        if (i % 10 === 0) return null; // Skip cm marks
        const leftPercent = i / (computed.displayLength * 10) * 100;
        const isFive = i % 5 === 0;
        return <div key={`mm-${i}`} className="absolute top-0 bg-amber-700" style={{
          left: `${leftPercent}%`,
          height: isFive ? '8px' : '5px',
          width: '1px',
          transform: 'translateX(-50%)'
        }} data-id="element-9933" />;
      })}
      </div>
    </div>;
  if (mode === 'stimulus') {
    return <div className="bg-white rounded-xl p-4 border border-slate-200 w-full max-w-sm mx-auto" data-id="element-9934">
        {label && <p className="text-sm font-semibold text-slate-700 mb-3 text-center" data-id="element-9935">
            {label}
          </p>}
        {rulerViz}
      </div>;
  }
  return <div className="bg-white rounded-xl border border-amber-100 overflow-hidden w-full max-w-sm mx-auto shadow-sm" data-id="element-9936">
      <div className="bg-amber-50 px-4 py-3 border-b border-amber-100" data-id="element-9937">
        <h3 className="text-sm font-bold text-amber-900 text-center" data-id="element-9938">
          {label || 'Liniaal'}
        </h3>
      </div>

      <div className="p-6 bg-white" data-id="element-9939">{rulerViz}</div>

      <div className="bg-amber-50/50 p-4 border-t border-amber-50" data-id="element-9940">
        <p className="text-sm text-slate-700 text-center leading-relaxed" data-id="element-9941">
          {computed.hasObject ? <>
              Het blok begint bij{' '}
              <strong data-id="element-9942">
                {computed.start} {unit}
              </strong>{' '}
              en eindigt bij{' '}
              <strong data-id="element-9943">
                {computed.end} {unit}
              </strong>
              . Lengte = {computed.end} − {computed.start} ={' '}
              <strong data-id="element-9944">
                {computed.objectLength} {unit}
              </strong>
              .
            </> : <>
              Dit is een liniaal om lengtes te meten. Elk groot streepje is 1
              cm.
            </>}
        </p>
      </div>
    </div>;
}